import { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { useAdminMe } from '../../lib/auth';
import { SplashLoader } from '../../components/SplashLoader';
import { queryClient } from '../../lib/queryClient';
import logoWhite from '../../assets/logo-white.svg';
import styles from './AdminLoginPage.module.css';

const ERR_MSG: Record<string, string> = {
  domain: "Your Google account isn't in the @gokick.com workspace — sign in with a @gokick.com address.",
  email_unverified: "Your Google email isn't verified yet. Verify it with Google and try again.",
  rejected: 'Sign-in is not allowed for this account.',
  server: 'Sign-in failed. Please try again.',
};

export function AdminLoginPage() {
  const me = useAdminMe();
  const [params] = useSearchParams();
  const errKey = params.get('error');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    document.body.style.background = '#1e2347';
    return () => {
      document.body.style.background = '';
    };
  }, []);

  if (me.isPending) return <SplashLoader />;
  if (signedIn || (!me.isError && me.data)) return <Navigate to="/admin" replace />;
  if (me.isError && !(me.error instanceof ApiError && me.error.status === 401)) {
    return <SplashLoader />;
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    setErr('');
    if (!email.trim() || !password) {
      setErr('Email and password are required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/admin/login', { email: email.trim(), password });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'me'] });
      setSignedIn(true);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.body}>
      <div className={styles.card}>
        <div className={styles.logoRow}>
          <img src={logoWhite} alt="Kick" className={styles.logo} />
          <span className={styles.logoText}>Creator Outreach Portal</span>
        </div>
        <h1 className={styles.title}>Sign in</h1>
        <p className={styles.subtitle}>Use your @gokick.com Google account, or email + password.</p>
        {errKey && <div className={styles.errorMsg}>{ERR_MSG[errKey] ?? 'Sign-in failed.'}</div>}
        {err && <div className={styles.errorMsg}>{err}</div>}

        <form onSubmit={submit}>
          <label className={styles.label} htmlFor="admin-email">
            Email
          </label>
          <input
            id="admin-email"
            type="email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="you@gokick.com"
          />
          <label className={styles.label} htmlFor="admin-password">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            className={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="••••••••"
          />
          <button type="submit" className={styles.btn} disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className={styles.divider}>or</div>
        <a className={styles.btn} href="/auth/admin/google/start">
          Continue with Google
        </a>
        <p className={styles.footerNote}>Access restricted to authorized team members</p>
      </div>
    </div>
  );
}
